<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'

import { AppIcon } from '@/components/shared'

/**
 * Date picker with a custom CZ-formatted calendar dropdown. Replaces the
 * native <input type="date"> which renders inconsistently across OSes
 * and looks plain on dark themes.
 *
 * v-model contract is the ISO date string (YYYY-MM-DD). That keeps the
 * Laravel form payload identical to what <input type="date"> used to ship
 * - no controller / validation changes needed when swapping the input.
 *
 * Display formatting goes through Intl.DateTimeFormat('cs-CZ') so the
 * trigger button reads "25. května 2026", the month header reads
 * "květen 2026", and weekday headers say "po, út, st …". No string
 * literals for Czech month/weekday names - locale data wins.
 *
 * Accessibility:
 *   - Trigger is a real button, opens an aria-modal grid dialog.
 *   - Each day cell is a button with aria-pressed for the selected day
 *     and aria-current="date" for today.
 *   - Roving tabindex pattern via `focusedDate`; ArrowKeys/PageUp/Down/
 *     Home/End move focus; Enter/Space pick; Esc closes.
 *   - Year jump arrows let the user skip 12 months in one click.
 */
const props = defineProps({
    /** ISO date string YYYY-MM-DD, or '' / null for unset. */
    modelValue: {
        type: [String, null],
        default: '',
    },
    /** Optional id for label-for + error-id derivation. */
    id: {
        type: String,
        default: null,
    },
    error: {
        type: String,
        default: null,
    },
    placeholder: {
        type: String,
        default: 'Vyber datum…',
    },
    /** Lower bound - pass an ISO date string to disable earlier days. */
    min: {
        type: String,
        default: null,
    },
    /** Upper bound - pass an ISO date string to disable later days. */
    max: {
        type: String,
        default: null,
    },
})

const emit = defineEmits(['update:modelValue', 'change'])

const open = ref(false)
const rootRef = ref(null)

const errorId = computed(() => (props.id ? `${props.id}-error` : undefined))

// ---------- ISO ⇄ Date helpers ----------
// Always work in local time so a user clicking "25 May" gets "2026-05-25"
// regardless of their UTC offset. Using `new Date(iso)` would parse as
// UTC midnight and shift the day for anyone west of London.
function parseIso(iso) {
    if (!iso || typeof iso !== 'string') return null
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (!m) return null
    const [, y, mo, d] = m
    return new Date(Number(y), Number(mo) - 1, Number(d))
}

function toIso(date) {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return ''
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, '0')
    const d = String(date.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
}

function sameDay(a, b) {
    if (!a || !b) return false
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

// ---------- Reactive view state ----------
// `viewYear` + `viewMonth` drive the grid; they're decoupled from the
// selected value so the user can pan around without changing the value
// until they pick a day.
const today = new Date()
const selectedDate = computed(() => parseIso(props.modelValue))

const viewYear = ref((selectedDate.value ?? today).getFullYear())
const viewMonth = ref((selectedDate.value ?? today).getMonth())
const focusedDate = ref(selectedDate.value ?? new Date(viewYear.value, viewMonth.value, today.getDate()))

watch(
    () => props.modelValue,
    (val) => {
        const d = parseIso(val)
        if (d) {
            viewYear.value = d.getFullYear()
            viewMonth.value = d.getMonth()
            focusedDate.value = d
        }
    }
)

// ---------- Calendar grid math ----------
// CZ weeks start on Monday. JS getDay() is 0=Sun..6=Sat; remap so 0=Mon.
function weekdayMon(date) {
    return (date.getDay() + 6) % 7
}

const monthGrid = computed(() => {
    const firstOfMonth = new Date(viewYear.value, viewMonth.value, 1)
    const leadingBlanks = weekdayMon(firstOfMonth)
    const daysInMonth = new Date(viewYear.value, viewMonth.value + 1, 0).getDate()
    // 6 weeks x 7 days = 42 cells - fits the maximum-stretch month layout
    // (Feb 29 starting on Sunday spills 5 rows, fine; longer months max at 6).
    const cells = []
    for (let i = 0; i < leadingBlanks; i++) {
        const date = new Date(viewYear.value, viewMonth.value, -leadingBlanks + i + 1)
        cells.push({ date, inMonth: false })
    }
    for (let d = 1; d <= daysInMonth; d++) {
        cells.push({ date: new Date(viewYear.value, viewMonth.value, d), inMonth: true })
    }
    while (cells.length % 7 !== 0 || cells.length < 42) {
        const last = cells[cells.length - 1].date
        const next = new Date(last)
        next.setDate(next.getDate() + 1)
        cells.push({ date: next, inMonth: false })
        if (cells.length === 42) break
    }
    return cells
})

// ---------- Locale-formatted strings ----------
const monthLabel = computed(() => {
    const d = new Date(viewYear.value, viewMonth.value, 1)
    const fmt = new Intl.DateTimeFormat('cs-CZ', { month: 'long', year: 'numeric' })
    // First-letter capitalisation - Intl returns "květen 2026" lowercase.
    const raw = fmt.format(d)
    return raw.charAt(0).toUpperCase() + raw.slice(1)
})

// Weekday headers: po, út, st, čt, pá, so, ne - generated from Intl so
// the abbreviations stay locale-correct if we ever add more locales.
const weekdayLabels = computed(() => {
    const fmt = new Intl.DateTimeFormat('cs-CZ', { weekday: 'short' })
    // 2026-05-25 is a Monday - use a known anchor week to enumerate.
    const monday = new Date(2026, 4, 25)
    return Array.from({ length: 7 }, (_, i) => {
        const d = new Date(monday)
        d.setDate(monday.getDate() + i)
        return fmt.format(d).replace('.', '')
    })
})

const triggerLabel = computed(() => {
    if (!selectedDate.value) return props.placeholder
    return new Intl.DateTimeFormat('cs-CZ', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    }).format(selectedDate.value)
})

// ---------- Min / Max bounds ----------
const minDate = computed(() => parseIso(props.min))
const maxDate = computed(() => parseIso(props.max))

function isDisabled(date) {
    if (minDate.value && date < minDate.value) return true
    if (maxDate.value && date > maxDate.value) return true
    return false
}

// ---------- Navigation ----------
function shiftMonth(delta) {
    let m = viewMonth.value + delta
    let y = viewYear.value
    while (m < 0) {
        m += 12
        y -= 1
    }
    while (m > 11) {
        m -= 12
        y += 1
    }
    viewMonth.value = m
    viewYear.value = y
}

function shiftYear(delta) {
    viewYear.value += delta
}

function jumpToday() {
    viewYear.value = today.getFullYear()
    viewMonth.value = today.getMonth()
    focusedDate.value = new Date(today.getFullYear(), today.getMonth(), today.getDate())
}

function selectDate(date) {
    if (isDisabled(date)) return
    emit('update:modelValue', toIso(date))
    emit('change', toIso(date))
    closePanel()
}

function openPanel() {
    if (open.value) return
    open.value = true
    nextTick(() => {
        const focused = rootRef.value?.querySelector('[data-day-focused="true"]')
        focused?.focus()
    })
}

function closePanel() {
    open.value = false
    nextTick(() => rootRef.value?.querySelector('[data-datepicker-trigger]')?.focus())
}

function togglePanel() {
    if (open.value) closePanel()
    else openPanel()
}

function onDayKeydown(event) {
    const d = focusedDate.value
    if (!d) return
    let next = null
    switch (event.key) {
        case 'ArrowLeft':
            next = new Date(d)
            next.setDate(d.getDate() - 1)
            break
        case 'ArrowRight':
            next = new Date(d)
            next.setDate(d.getDate() + 1)
            break
        case 'ArrowUp':
            next = new Date(d)
            next.setDate(d.getDate() - 7)
            break
        case 'ArrowDown':
            next = new Date(d)
            next.setDate(d.getDate() + 7)
            break
        case 'PageUp':
            next = new Date(d)
            next.setMonth(d.getMonth() - (event.shiftKey ? 12 : 1))
            break
        case 'PageDown':
            next = new Date(d)
            next.setMonth(d.getMonth() + (event.shiftKey ? 12 : 1))
            break
        case 'Home':
            next = new Date(d)
            next.setDate(1)
            break
        case 'End':
            next = new Date(d.getFullYear(), d.getMonth() + 1, 0)
            break
        case 'Enter':
        case ' ':
            event.preventDefault()
            selectDate(d)
            return
        case 'Escape':
            event.preventDefault()
            closePanel()
            return
        default:
            return
    }
    if (next) {
        event.preventDefault()
        focusedDate.value = next
        viewYear.value = next.getFullYear()
        viewMonth.value = next.getMonth()
        nextTick(() => rootRef.value?.querySelector('[data-day-focused="true"]')?.focus())
    }
}

function onDocumentClick(event) {
    if (!open.value) return
    if (rootRef.value && !rootRef.value.contains(event.target)) {
        open.value = false
    }
}

watch(open, (val) => {
    if (val) document.addEventListener('mousedown', onDocumentClick)
    else document.removeEventListener('mousedown', onDocumentClick)
})

onBeforeUnmount(() => {
    document.removeEventListener('mousedown', onDocumentClick)
})
</script>

<template>
    <div
        class="relative"
        ref="rootRef"
    >
        <button
            class="bg-surface-container-high text-on-surface focus:border-primary-container focus:ring-primary-container/70 flex w-full items-center justify-between gap-3 rounded-md border p-4 text-left transition-all focus:ring-2 focus:outline-none"
            :class="error ? 'border-error' : 'border-zinc-700'"
            :id="id"
            type="button"
            data-datepicker-trigger
            aria-haspopup="dialog"
            :aria-expanded="open"
            :aria-invalid="error ? 'true' : undefined"
            :aria-describedby="error ? errorId : undefined"
            @click="togglePanel"
        >
            <span :class="selectedDate ? 'text-on-surface' : 'text-zinc-500'">
                {{ triggerLabel }}
            </span>
            <AppIcon
                class="shrink-0 text-zinc-400"
                name="calendar_month"
            />
        </button>

        <!--
            Calendar dropdown. role="dialog" + aria-modal=false because the
            page behind is still operable - closing via outside click or Esc
            is fine for a date picker (not a destructive modal).
        -->
        <div
            class="bg-surface-container-high absolute z-30 mt-2 w-full max-w-90 overflow-hidden rounded-md border border-zinc-700 p-4 shadow-2xl shadow-black/60"
            v-if="open"
            role="dialog"
            aria-label="Vyber datum"
        >
            <!-- Header - year + month nav. Year arrows skip 12 months,
                 month arrows skip one. Center label is locale-formatted. -->
            <div class="mb-4 flex items-center justify-between gap-2">
                <div class="flex items-center gap-1">
                    <button
                        class="text-on-surface-variant hover:bg-surface-container-highest hover:text-on-surface flex h-8 w-8 items-center justify-center rounded-md transition-colors"
                        type="button"
                        :aria-label="`Předchozí rok (${viewYear - 1})`"
                        @click="shiftYear(-1)"
                    >
                        <AppIcon
                            class="text-base"
                            name="keyboard_double_arrow_left"
                        />
                    </button>
                    <button
                        class="text-on-surface-variant hover:bg-surface-container-highest hover:text-on-surface flex h-8 w-8 items-center justify-center rounded-md transition-colors"
                        type="button"
                        aria-label="Předchozí měsíc"
                        @click="shiftMonth(-1)"
                    >
                        <AppIcon
                            class="text-base"
                            name="chevron_left"
                        />
                    </button>
                </div>
                <p
                    class="teko-font text-on-surface text-base font-bold tracking-wide uppercase"
                    aria-live="polite"
                >
                    {{ monthLabel }}
                </p>
                <div class="flex items-center gap-1">
                    <button
                        class="text-on-surface-variant hover:bg-surface-container-highest hover:text-on-surface flex h-8 w-8 items-center justify-center rounded-md transition-colors"
                        type="button"
                        aria-label="Další měsíc"
                        @click="shiftMonth(1)"
                    >
                        <AppIcon
                            class="text-base"
                            name="chevron_right"
                        />
                    </button>
                    <button
                        class="text-on-surface-variant hover:bg-surface-container-highest hover:text-on-surface flex h-8 w-8 items-center justify-center rounded-md transition-colors"
                        type="button"
                        :aria-label="`Další rok (${viewYear + 1})`"
                        @click="shiftYear(1)"
                    >
                        <AppIcon
                            class="text-base"
                            name="keyboard_double_arrow_right"
                        />
                    </button>
                </div>
            </div>

            <!-- Weekday header row. aria-hidden because each day button
                 already announces its full date via aria-label. -->
            <div
                class="mb-1 grid grid-cols-7 gap-1 text-center"
                aria-hidden="true"
            >
                <span
                    class="text-[10px] font-bold tracking-widest text-zinc-500 uppercase"
                    v-for="day in weekdayLabels"
                    :key="day"
                >
                    {{ day }}
                </span>
            </div>

            <!-- Day grid. role="grid" makes screen readers announce the 7x6
                 structure; each cell is a real button so keyboard nav +
                 click both work. -->
            <div
                class="grid grid-cols-7 gap-1"
                role="grid"
                @keydown="onDayKeydown"
            >
                <button
                    class="flex aspect-square items-center justify-center rounded-md text-sm transition-colors"
                    v-for="cell in monthGrid"
                    :key="cell.date.toISOString()"
                    type="button"
                    :tabindex="sameDay(cell.date, focusedDate) ? 0 : -1"
                    :data-day-focused="sameDay(cell.date, focusedDate)"
                    :aria-pressed="sameDay(cell.date, selectedDate)"
                    :aria-current="sameDay(cell.date, today) ? 'date' : undefined"
                    :aria-label="
                        new Intl.DateTimeFormat('cs-CZ', { day: 'numeric', month: 'long', year: 'numeric' }).format(
                            cell.date
                        )
                    "
                    :disabled="isDisabled(cell.date)"
                    :class="{
                        'bg-primary-container text-on-primary-container font-bold': sameDay(cell.date, selectedDate),
                        'ring-primary-container/50 ring-1':
                            sameDay(cell.date, today) && !sameDay(cell.date, selectedDate),
                        'text-on-surface hover:bg-surface-container-highest':
                            cell.inMonth && !sameDay(cell.date, selectedDate) && !isDisabled(cell.date),
                        'text-zinc-600': !cell.inMonth && !isDisabled(cell.date),
                        'cursor-not-allowed text-zinc-700': isDisabled(cell.date),
                    }"
                    @click="selectDate(cell.date)"
                    @focus="focusedDate = cell.date"
                >
                    {{ cell.date.getDate() }}
                </button>
            </div>

            <!-- Footer shortcut: "Dnes" jumps back to today's month + focuses
                 today's cell. Helps when a photographer paged far away. -->
            <div class="mt-3 flex justify-between border-t border-zinc-800 pt-3">
                <button
                    class="teko-font text-primary-container hover:text-primary-container/80 text-xs font-bold tracking-widest uppercase transition-colors"
                    type="button"
                    @click="jumpToday"
                >
                    Dnes
                </button>
                <button
                    class="teko-font text-on-surface-variant hover:text-on-surface text-xs font-medium tracking-widest uppercase transition-colors"
                    type="button"
                    @click="closePanel"
                >
                    Zavřít
                </button>
            </div>
        </div>
    </div>
</template>
